import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import useFetch from "../../hooks/useFetch";

export default function PDPartners() {
    const authState = useSelector((state) => state.authReducer);
    const [fetchData] = useFetch();
    const navigate = useNavigate();
    const userCode = authState?.user?.think5whyscode || "";
    const token = authState?.token;

    const [tab, setTab] = useState("DIRECTOR");
    const [subTab, setSubTab] = useState("onboarded");

    const [onboardings, setOnboardings] = useState([]);
    const [subscriptions, setSubscriptions] = useState([]);

    const [loading, setLoading] = useState(false);

    const [message, setMessage] = useState({ text: '', type: '' });
    
    const showMessage = (text, type = "success") => {
        setMessage({ text, type });
    };

    const tabs = [
        "DIRECTOR",
        "COORDINATOR",
        "INFLUENCER",
        "INSTITUTION",
        "MENTOR",
    ];

    const fetchOnboardings = async (partner) => {
      setLoading(true);

      try {
          const config = {
              url: "/pd/onboarding/list",
              method: "post",
              data: { userCode, partner },
              headers: { Authorization: authState.token },
          };

            const response = await fetchData(config, { silent: true });
          if (response?.status) {
              setOnboardings(response.records || []);
          }
      } catch (err) {
          console.error("❌ Onboarding fetch error:", err);
          showMessage("Failed to load tasks", "error");
      }

      setLoading(false);
    };


    const fetchSubscriptions = async (partner) => {
      setLoading(true);

      try {
          const config = {
              url: "/pd/subscription/revenue",
              method: "post",
              data: { userCode, partner },
              headers: { Authorization: authState.token },
          };

            const response = await fetchData(config, { silent: true });
          if (response?.status) {
              setSubscriptions(response.records || []);
          }
      } catch (err) {
          console.error("❌ Onboarding fetch error:", err);
          showMessage("Failed to load tasks", "error");
      }

      setLoading(false);
    };

    // FILTERED ONBOARDINGS (optional safety layer)
    const filteredOnboardings = useMemo(() => {
        return onboardings;
    }, [onboardings]);

    // REVENUE TOTAL
    const revenueTotal = useMemo(() => {
        return subscriptions.reduce((acc, r) => {
            if (tab === "DIRECTOR") return acc + (r.feeDirector || 0);
            if (tab === "COORDINATOR") return acc + (r.feeCoordinator || 0);
            if (tab === "INFLUENCER") return acc + (r.feeInfluencer || 0);
            if (tab === "OTHERS") return acc + (r.reservePool || 0);
            return acc;
        }, 0);
    }, [subscriptions, tab]);

    const subscriptionTotal = useMemo(() => {
        return subscriptions.reduce(
            (acc, r) => acc + Number(r.baseAmount || 0),
            0
        );
    }, [subscriptions]);

    const showRevenueTab =
        tab === "DIRECTOR" ||
        tab === "COORDINATOR" ||
        tab === "INFLUENCER";

    return (
      <div className="space-y-6 bg-gray-900 p-6 rounded-xl text-white border-t border-cyan-200 mt-2">
        <h1 className="mt-2 mb-2 text-cyan-200">
          DMD, DC & DI Individual Users & Institution Admin
        </h1>
        <div className="grid grid-cols-3 lg:grid-cols-5 gap-1 bg-gray-900 text-xs mb-2">
          {tabs.map((t) => (
            <button
              key={t}
              onClick={() => {
                setTab(t);
                setSubTab("onboarded");
              }}
              className={`px-2 py-1 text-[10px] ${
                tab === t
                  ? "text-cyan-400 bg-blue-600"
                  : "text-gray-400 bg-gray-700"
              }`}
            >
              <span className="text-[10px]">{t}</span>
            </button>
          ))}
        </div>
        <div>
          <button
            onClick={() => navigate("/foundher/badge/admin")}
            className="w-fit flex items-center gap-1.5 px-3 py-1.5 mt-2 rounded-full text-xs font-medium bg-violet-600 text-white hover:bg-violet-500 transition"
          >
            FoundHer Badge Approver
            <span aria-hidden="true">→</span>
          </button>
        </div>

        {/* SUB TABS */}
        <div className="flex gap-2 mb-3 text-xs">
          <button
            onClick={() => {
              setSubTab("onboarded");
              fetchOnboardings(tab);
            }}
            className={`w-fit px-3 py-1 bg-cyan-600 text-white rounded ${
              subTab === "onboarded"
                ? "text-white bg-blue-600"
                : "text-gray-400 bg-gray-700"
            }`}
          >
            Onboarded List
          </button>

          {showRevenueTab && (
            <button
              onClick={() => {
                setSubTab("revenue");
                fetchSubscriptions(tab);
              }}
              className={`w-fit px-3 py-1 rounded ${
                subTab === "revenue"
                  ? "bg-cyan-600"
                  : "bg-gray-800 text-gray-400"
              }`}
            >
              My Revenue
            </button>
          )}
        </div>

        {/* LOADING */}
        {loading && (
          <div className="text-xs text-gray-400 mb-2">Loading...</div>
        )}

        {/* ONBOARDED LIST */}
        {subTab === "onboarded" && (
          <div className="overflow-auto">
            <table className="w-full text-xs border border-gray-700">
              <thead className="bg-gray-900 text-gray-300">
                <tr>
                  <th className="p-2">User Code</th>
                  <th className="p-2">User Name</th>
                  <th className="p-2">Email</th>
                  <th className="p-2">Roles</th>
                  <th className="p-2">Active</th>
                </tr>
              </thead>

              <tbody>
                {filteredOnboardings.map((u, i) => (
                  <tr key={i} className="border-t border-gray-800">
                    <td className="p-2">{u.userCode}</td>
                    <td className="p-2">{u.userName}</td>
                    <td className="p-2">{u.emailId || "-"}</td>
                    <td className="p-2">{u.roles?.join(", ")}</td>
                    <td className="p-2">{u.isActive ? "Yes" : "No"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* REVENUE */}
        {subTab === "revenue" && showRevenueTab && (
          <div className="text-xs">
            <div className="bg-gray-900 p-3 rounded mb-3">
              <div className="text-gray-400 text-xs">Total Revenue</div>
              <div className="text-cyan-400 font-bold text-lg">
                {revenueTotal.toFixed(2)}
              </div>
            </div>
            <table className="w-full text-xs border border-gray-700">
              <thead className="bg-gray-900 text-gray-300">
                <tr>
                  <th className="p-2">Product</th>
                  <th className="p-2">User Code</th>
                  <th className="p-2">Basic Amount</th>

                  {tab === "DIRECTOR" && <th className="p-2">Director Fee</th>}

                  {(tab === "DIRECTOR" || tab === "COORDINATOR") && (
                    <th className="p-2">Coordinator Fee</th>
                  )}

                  {(tab === "DIRECTOR" || tab === "INFLUENCER") && (
                    <th className="p-2">Influencer Fee</th>
                  )}

                  {tab === "DIRECTOR" && <th className="p-2">Reserve Pool</th>}
                </tr>
              </thead>

              <tbody>
                {subscriptions.map((r, i) => (
                  <tr key={i} className="border-t border-gray-800">
                    <td className="p-2">{r.product}</td>
                    <td className="p-2">{r.userCode}</td>
                    <td className="p-2">{r.baseAmount}</td>

                    {tab === "DIRECTOR" && (
                      <td className="p-2">{r.feeDirector}</td>
                    )}

                    {(tab === "DIRECTOR" || tab === "COORDINATOR") && (
                      <td className="p-2">{r.feeCoordinator}</td>
                    )}

                    {(tab === "DIRECTOR" || tab === "INFLUENCER") && (
                      <td className="p-2">{r.feeInfluencer}</td>
                    )}

                    {tab === "DIRECTOR" && (
                      <td className="p-2">{r.reservePool}</td>
                    )}
                  </tr>
                ))}
              </tbody>
              {/* //Show Column Total                        
// Also show either small font or horizontal scroll as its truncating on mobile if column exceed */}
            </table>
          </div>
        )}
      </div>
    );
}